import {useState} from 'react';
import {useForm} from 'react-hook-form';
import cx from 'classnames';

import {BirthSex, MeasurementUnit} from '#apollo/schema';
import {Button, Form, Text} from '#component/base';
import {ModalRoot, ModalTrigger} from '#component/base/Modal';
import {ContactUsModal} from '#component/modal';
import {NumberInputField, TextInputField} from '#component/partial';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {SUPPORT_EMAIL} from '#constant/app';
import {PRONOUN_OPTIONS} from '#constant/prounouns';
import {sprinkles} from '#style/sprinkles';
import {convertPoundToKilogram, handleValidationError} from '#util';
import {convertFeetStringToCm} from '#util/height';
import {ValidationError} from '#util/validator/handleValidationError';

import {CombinedMeasurementInputField} from './component';
import {getFormConfig} from './EditProfileForm.util';

import {EditProfileFormProps, FieldValues, SubmitHandler} from './EditProfileForm.types';

import * as css from './EditProfileForm.css';

export const EditProfileForm = ({
  className,
  onError,
  onSubmit,
  ...defaultValues
}: EditProfileFormProps) => {
  const {track} = useAnalytics();
  const {
    setError,
    clearErrors,
    formState: {errors, isSubmitting},
    handleSubmit,
    register,
    getValues,
    setValue,
    watch,
  } = useForm<FieldValues>(getFormConfig(defaultValues));

  const [showBirthSexMessage, setShowBirthSexMessage] = useState(
    getValues('birthSex') === BirthSex.Unknown,
  );

  const isHeightMetric = watch('heightDisplayUnit') === MeasurementUnit.Metric;
  const isWeightMetric = watch('weightDisplayUnit') === MeasurementUnit.Metric;
  const heightDisplayUnitProps = register('heightDisplayUnit');
  const weightDisplayUnitProps = register('weightDisplayUnit');

  const handleEditProfile: SubmitHandler = async ({
    birthSex,
    firstName,
    height,
    heightDisplayUnit,
    lastName,
    pronouns,
    weight,
    weightDisplayUnit,
  }) => {
    const parsedHeight = isHeightMetric
      ? (height as number)
      : convertFeetStringToCm(height as string);

    const parsedWeight = isWeightMetric ? weight : convertPoundToKilogram(weight);

    try {
      await onSubmit({
        birthSex,
        firstName,
        height: parsedHeight,
        heightDisplayUnit,
        lastName,
        pronouns,
        weight: parsedWeight,
        weightDisplayUnit,
      });

      track<UserActionEvent>({
        evtName: EventType.UserAction,
        actionName: 'Profile change success',
      });
    } catch (error) {
      handleValidationError({
        error: error as unknown as ValidationError<FieldValues>,
        errorMessages: {
          form: 'Unable to edit your profile information. Please check the entered data and try again.',
        },
        setError,
      });
      onError?.(error as unknown as Error);
    }
  };

  const trackPronounFocus = () => {
    track<UserActionEvent>({
      evtName: EventType.UserAction,
      actionName: 'View edit pronoun',
    });
  };

  const trackBirthSexFocus = () => {
    track<UserActionEvent>({
      evtName: EventType.UserAction,
      actionName: 'View edit birth sex',
    });
  };

  return (
    <Form
      className={cx(sprinkles({gap: 80}), className)}
      clearErrors={clearErrors}
      errors={errors}
      onSubmit={handleSubmit(handleEditProfile)}>
      <div className={sprinkles({flex: 'flex', direction: 'col', gap: 32})}>
        <Text as="h2" variant="heading4">
          Personal information
        </Text>
        <div className={css.formSection}>
          <TextInputField errors={errors} label="First name" {...register('firstName')} />

          <TextInputField errors={errors} label="Last name" {...register('lastName')} />

          <NumberInputField
            errors={errors}
            label="Birthday"
            mask="99/99/9999"
            maskPlaceholder="mm/dd/yyyy"
            {...register('birthday')}
            value={defaultValues.birthday || undefined}
            disabled
          />

          <TextInputField
            errors={errors}
            label="Birth sex"
            message={(htmlInputId: string) => {
              return showBirthSexMessage ? (
                <Text as="label" variant="caption" color="doveGray" htmlFor={htmlInputId}>
                  Adding your birth sex helps us improve data accuracy
                </Text>
              ) : null;
            }}
            type="select"
            options={[
              {value: BirthSex.Unknown, label: 'Prefer not to say'},
              {value: BirthSex.Female, label: 'Female'},
              {value: BirthSex.Male, label: 'Male'},
            ]}
            onFocus={trackBirthSexFocus}
            {...register('birthSex', {
              onChange: () => {
                setShowBirthSexMessage(getValues('birthSex') === BirthSex.Unknown);
              },
            })}
          />
        </div>
      </div>

      <div className={sprinkles({flex: 'flex', direction: 'col', gap: 32})}>
        <div>
          <Text as="h2" variant="heading4">
            Additional information
          </Text>
          <Text as="p" className={sprinkles({mT: 4})} variant="caption" color="doveGray">
            Optional
          </Text>
        </div>

        <div className={css.bioFormSection}>
          <TextInputField
            errors={errors}
            label="Pronouns"
            type="select"
            options={PRONOUN_OPTIONS}
            onFocus={trackPronounFocus}
            {...register('pronouns')}
          />

          <CombinedMeasurementInputField
            label="Height"
            numberInputProps={{
              errors: errors,
              mask: isHeightMetric ? '' : '9\'99"',
              maskPlaceholder: 'x\'xx"',
              placeholder: isHeightMetric ? '' : 'x\'xx"',
              step: isHeightMetric ? '0.01' : 'undefined',
              type: isHeightMetric ? 'number' : 'text',
              ...register('height'),
            }}
            selectInputProps={{
              label: 'Height Measurement Unit',
              options: [
                {value: MeasurementUnit.Imperial, label: 'ft'},
                {value: MeasurementUnit.Metric, label: 'cm'},
              ],
              ...heightDisplayUnitProps,
              onChange: e => {
                heightDisplayUnitProps.onChange(e);
                setValue('height', '');
              },
            }}
          />

          <CombinedMeasurementInputField
            label="Weight"
            numberInputProps={{
              errors,
              mask: '',
              step: '0.01',
              type: 'number',
              ...register('weight'),
            }}
            selectInputProps={{
              label: 'Weight Measurement Unit',
              errors,
              options: [
                {value: MeasurementUnit.Imperial, label: 'lbs'},
                {value: MeasurementUnit.Metric, label: 'kg'},
              ],
              ...weightDisplayUnitProps,
              onChange: e => {
                weightDisplayUnitProps.onChange(e);
                setValue('weight', 0);
              },
            }}
          />
        </div>
      </div>

      <footer className={sprinkles({flex: 'flex', direction: 'col', gap: 32})}>
        <div className={sprinkles({flex: 'flex', gap: [12, 12, 32], dir: ['col', 'col', 'row']})}>
          <ModalRoot>
            <Button as={ModalTrigger} type="button" variant="secondary">
              Contact Us
            </Button>
            <ContactUsModal />
          </ModalRoot>

          <Button disabled={isSubmitting} type="submit" icon="Check">
            Confirm
          </Button>
        </div>

        <Text as="p" textAlign="center" color="doveGray" fontWeight="light">
          {'If you need to update a locked field above, contact us at '}
          <Text as="a" href={`mailto:${SUPPORT_EMAIL}`} color="doveGray" fontWeight="semiBold">
            {SUPPORT_EMAIL}
          </Text>
          {' with the email you use to access this account.'}
        </Text>
      </footer>
    </Form>
  );
};
